import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  AlertTitle,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { EmailOutlined, LockOutlined } from '@mui/icons-material';
import { useIntl } from 'react-intl';
import { useSetClientPinSelfMutation } from '../queries';
import { IconBinocular } from '@shared/components/icons/icon-binocular';

export const SetPinPage = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [showPin2, setShowPin2] = useState(false);
  const [pinConfirm, setPinConfirm] = useState('');
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const { mutate: savePin, isPending } = useSetClientPinSelfMutation();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (pin.length < 4) {
      setFormError(
        intl.formatMessage({
          id: 'customerAuth.setPinTooShort',
          defaultMessage: 'PIN musí mít alespoň 4 znaky.',
        }),
      );
      return;
    }

    if (pin !== pinConfirm) {
      setFormError(
        intl.formatMessage({
          id: 'customerAuth.setPinMismatch',
          defaultMessage: 'PINy se neshodují.',
        }),
      );
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setFormError('Zadejte platný e-mail pro případný reset PINu.');
      return;
    }

    savePin(
      { pin, email: email.trim() },
      {
        onSuccess: () => {
          navigate('/customer-reservations/booking', { replace: true });
        },
        onError: error => {
          setFormError(error.message);
        },
      },
    );
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Stack spacing={3}>
          <Typography variant="h5" textAlign="center" fontWeight={700}>
            {intl.formatMessage({
              id: 'customerAuth.setPinTitle',
              defaultMessage: '🔐 Nastavte si PIN',
            })}
          </Typography>

          <Alert severity="warning">
            <AlertTitle sx={{ fontWeight: 700 }}>
              {intl.formatMessage({
                id: 'customerAuth.setPinWarningTitle',
                defaultMessage: 'Důležité upozornění',
              })}
            </AlertTitle>
            {intl.formatMessage({
              id: 'customerAuth.setPinWarningText',
              defaultMessage:
                'Tento PIN budete potřebovat pro příští přihlášení. Zadejte také e-mail, na který vám můžeme poslat odkaz pro jeho reset.',
            })}
          </Alert>

          {formError && (
            <Alert severity="error" onClose={() => setFormError(null)}>
              {formError}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label="E-mail pro reset PINu"
                value={email}
                onChange={e => setEmail(e.target.value)}
                type="email"
                autoComplete="email"
                required
                fullWidth
                disabled={isPending}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Stack spacing={2} direction="row" alignItems="center">
                <TextField
                  label={intl.formatMessage({
                    id: 'customerAuth.setPinLabel',
                    defaultMessage: 'Váš PIN (min. 4 znaky)',
                  })}
                  value={pin}
                  onChange={e => setPin(e.target.value)}
                  type={showPin ? 'text' : 'password'}
                  autoComplete="new-password"
                  fullWidth
                  autoFocus
                  disabled={isPending}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <Button
                  variant="text"
                  onClick={() => setShowPin(prev => !prev)}
                  disabled={isPending}
                  sx={{ minWidth: '32px' }}
                >
                  <IconBinocular />
                </Button>
              </Stack>
              <Stack spacing={2} direction="row" alignItems="center">
                <TextField
                  label={intl.formatMessage({
                    id: 'customerAuth.setPinConfirmLabel',
                    defaultMessage: 'Potvrďte PIN',
                  })}
                  type={showPin2 ? 'text' : 'password'}
                  value={pinConfirm}
                  onChange={e => setPinConfirm(e.target.value)}
                  autoComplete="new-password"
                  fullWidth
                  disabled={isPending}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <Button
                  variant="text"
                  onClick={() => setShowPin2(prev => !prev)}
                  disabled={isPending}
                  sx={{ minWidth: '32px' }}
                >
                  <IconBinocular />
                </Button>
              </Stack>

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={isPending}
                startIcon={isPending ? <CircularProgress size={20} color="inherit" /> : undefined}
              >
                {intl.formatMessage({
                  id: 'customerAuth.setPinButton',
                  defaultMessage: 'Uložit PIN a pokračovat',
                })}
              </Button>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Container>
  );
};
